// Soulumo opening — straight into Explain. One box, one action.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadSimpleDraft, saveSimpleDraftLocal, saveSimpleDraftServer, clearSimpleDraft } from "./drafts";
import { TOK, SERIF, SANS, PrimaryBtn } from "./shared";
import { ExplainInput, MIN_EXPLAIN } from "./ExplainInput";
import { EXPLAIN_PLACEHOLDER } from "./TellStep";
import { RepeatCard, loadLastCreate } from "./RepeatBits";

export const STEP_NAMES = ["Explain", "Agree", "Record", "Create"];

export const SoulumoOpening = ({ onBegin, onRepeat, onResume, onBuildOwn }) => {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [hint, setHint] = useState(false);
  const [draft, setDraft] = useState(null);
  const [typed, setTyped] = useState(false);
  useEffect(() => {
    loadSimpleDraft().then((s) => {
      if (!s) return;
      if (!s.plan && s.step !== "list") setText(s.intent || "");
      else setDraft(s);
    });
  }, []);
  useEffect(() => {
    if (!typed || draft) return undefined;
    const state = { step: "tell", intent: text, saved_at: new Date().toISOString() };
    if (!text.trim()) { clearSimpleDraft(); return undefined; }
    saveSimpleDraftLocal(state);
    const id = setTimeout(() => saveSimpleDraftServer(state), 800);
    return () => clearTimeout(id);
  }, [text, typed]);
  const change = (v) => { setTyped(true); setText(v); setHint(false); };
  const [last] = useState(loadLastCreate);
  const canRepeat = last?.intent && last?.music_ids?.length;
  return (
    <section className="pt-10 pb-6 sm:pt-16 space-y-8" data-testid="soulumo-opening">
      <div className="opacity-0 animate-[fadeUp_0.7s_ease-out_forwards]">
        <p className="mb-3 text-[0.66rem] uppercase" style={{ fontFamily: SANS, color: TOK.gold, letterSpacing: "0.3em" }} data-testid="soulumo-create-kicker">Create with Soulumo</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]" style={{ fontFamily: SERIF, color: TOK.cream }}>
          What would you like to change?
        </h1>
        <p className="mt-5 max-w-md text-base" style={{ fontFamily: SANS, color: TOK.creamDim, lineHeight: 1.6 }} data-testid="soulumo-opening-sub">
          Tell Soulumo what&apos;s happening. You don&apos;t need to know what you need — we&apos;ll create it with you.
        </p>
      </div>
      {draft && (
        <div className="rounded-2xl p-4" style={{ background: "rgba(215,187,122,0.1)", border: `1px solid ${TOK.gold}` }} data-testid="simple-resume">
          <p className="text-lg" style={{ color: TOK.cream, fontFamily: SERIF }}>Welcome back. Your last installation is saved, just as you left it.</p>
          {draft.intent && <p className="mt-1 text-sm italic line-clamp-2" style={{ color: TOK.creamMist }}>“{draft.intent.slice(0, 140)}{draft.intent.length > 140 ? "…" : ""}”</p>}
          <div className="mt-3 flex gap-2 flex-wrap">
            <button type="button" onClick={() => onResume(draft)} data-testid="simple-resume-yes" className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: TOK.gold, color: TOK.ink }}>Pick up where I left off</button>
            <button type="button" onClick={() => { clearSimpleDraft(); setDraft(null); }} data-testid="simple-resume-no" className="rounded-full px-4 py-2 text-xs" style={{ color: TOK.creamMist }}>Start fresh</button>
          </div>
        </div>
      )}
      <div className="opacity-0 animate-[fadeUp_0.7s_ease-out_forwards]" style={{ animationDelay: "140ms" }}>
        <ExplainInput value={text} onChange={change} placeholder={EXPLAIN_PLACEHOLDER} testId="soulumo-explain-input" />
      </div>
      <button type="button" onClick={() => nav("/soulumo/guide?help=1")} data-testid="soulumo-opening-guide-link"
        className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-[background-color] duration-200 hover:bg-[rgba(215,187,122,0.14)]"
        style={{ fontFamily: SERIF, color: TOK.cream, background: "rgba(215,187,122,0.08)", border: "1px solid rgba(215,187,122,0.35)" }}>
        <img src="/coaches/adelaida_calm.webp" alt="" className="w-11 h-11 rounded-full object-cover object-top shrink-0" />
        <span className="text-lg leading-snug"><span className="italic">Not sure what to say?</span> Talk it through with Adelaida <span style={{ color: TOK.gold }}>›</span></span>
      </button>
      <PrimaryBtn testId="soulumo-begin-btn" onClick={() => (text.trim().length < MIN_EXPLAIN ? setHint(true) : onBegin(text.trim()))}>Begin</PrimaryBtn>
      {hint && <p className="-mt-4 text-sm text-center" style={{ fontFamily: SANS, color: TOK.gold }} data-testid="soulumo-begin-hint">Tell Soulumo a little first. Type it in the box above, or tap the mic and say it.</p>}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm" style={{ fontFamily: SANS, color: TOK.creamDim }} data-testid="soulumo-other-ways">
        <span className="text-xs" style={{ color: TOK.creamMist }}>Or</span>
        <button type="button" onClick={onBuildOwn} data-testid="soulumo-build-own-link" className="underline underline-offset-4 decoration-[rgba(215,187,122,0.4)] transition-opacity duration-200 hover:opacity-80">Build your own</button>
        <span aria-hidden style={{ color: TOK.creamMist }}>·</span>
        <button type="button" onClick={() => nav("/signature-installations")} data-testid="soulumo-browse-signatures-link" className="underline underline-offset-4 decoration-[rgba(215,187,122,0.4)] transition-opacity duration-200 hover:opacity-80">Browse signature installations</button>
      </div>
      <p className="text-center text-[0.62rem] uppercase" style={{ fontFamily: SANS, color: TOK.creamMist, letterSpacing: "0.3em" }} data-testid="soulumo-opening-steps">
        {STEP_NAMES.join("  →  ")}
      </p>
      {canRepeat && <RepeatCard last={last} onRepeat={onRepeat} />}
    </section>
  );
};
